import React, { ReactNode, useMemo } from "react";
import { omit } from "lodash";
import moment, { Moment } from "moment";
import momentTz from "moment-timezone";
import { Layout } from "react-grid-layout";
import { SCHEDULER_ROUNDING } from "./constants";

export interface LayoutWithNode extends Layout {
  node: ReactNode;
}

export interface ScheduleLayoutWithNode extends DateRange {
  node: ReactNode;
  i: string;
  status?: string;
  shuffled?: boolean;
  isDraggable?: boolean;
  isResizable?: boolean;
}

export interface DateRange {
  endAt: moment.Moment;
  startAt: moment.Moment;
}

export const isReactElement = (obj: {}): obj is React.ReactElement<{}> => {
  return Object.prototype.hasOwnProperty.call(obj, "type");
};

export const roundToInterval = (
  interval: number,
  someMoment: moment.Moment,
) => {
  const roundedMinutes =
    Math.round(someMoment.clone().minute() / interval) * interval;
  return someMoment.clone().minute(roundedMinutes).second(0);
};

export const getTotalHours = ({ endAt, startAt }: DateRange) =>
  endAt.clone().add(1, "s").diff(startAt, "hours");

export const getTotalColumns = ({ endAt, startAt }: DateRange) => {
  const totalHours = getTotalHours({ endAt, startAt });
  const cols = totalHours * Math.round(60 / SCHEDULER_ROUNDING);
  return cols;
};

interface getStartAtProps {
  startedAt?: moment.Moment;
  dueAt?: moment.Moment;
  estimatedDuration: number | null;
  scheduledStartAt?: moment.Moment;
}

export const getStartAt = ({
  startedAt,
  dueAt,
  estimatedDuration,
  scheduledStartAt,
}: getStartAtProps) => {
  return (
    (startedAt && moment(startedAt)) ||
    (scheduledStartAt && moment(scheduledStartAt)) ||
    (dueAt &&
      estimatedDuration &&
      moment(dueAt).subtract(estimatedDuration, "minutes")) ||
    null
  );
};

interface getEndAtProps {
  startedAt?: moment.Moment;
  estimatedDuration: number | null;
  completedAt?: moment.Moment;
  dueAt?: moment.Moment;
}

export const getEndAt = ({
  startedAt,
  estimatedDuration,
  completedAt,
  dueAt,
}: getEndAtProps) => {
  let endAt = null;
  if (completedAt) endAt = moment(completedAt);
  else if (startedAt && !completedAt)
    endAt = estimatedDuration
      ? moment(startedAt).add(estimatedDuration, "minutes")
      : null;
  else endAt = dueAt ? moment(dueAt) : null;

  return endAt;
};

interface convertItemToGridProps {
  item: DateRange;
  cols: number;
  maxRange: DateRange;
  totalDiff: number;
}

export const convertItemToGrid = ({
  item,
  cols,
  maxRange: { startAt, endAt },
  totalDiff,
}: convertItemToGridProps) => {
  const isItemStartAtInGrid = startAt.isSameOrBefore(item.startAt);
  const itemStartAtDiff = isItemStartAtInGrid ? item.startAt.diff(startAt) : 0;
  const itemStartAtPercent = itemStartAtDiff / totalDiff;
  const itemStartAtX = itemStartAtPercent * cols;
  const isItemEndAtInGrid = endAt.isSameOrAfter(item.endAt);
  const itemEndAt = isItemEndAtInGrid ? item.endAt : endAt;
  const itemEndAtDiff = isItemStartAtInGrid
    ? itemEndAt.diff(item.startAt)
    : itemEndAt.diff(startAt);
  const itemEndAtPercent = itemEndAtDiff / totalDiff;
  const itemEndAtWidth = itemEndAtPercent * cols;
  return {
    itemStartAtX,
    itemEndAtWidth,
  };
};

interface convertToGridProps extends DateRange {
  cols: number;
  layout: ScheduleLayoutWithNode[];
}

export const convertToGrid = ({
  cols,
  endAt,
  layout,
  startAt,
}: convertToGridProps): LayoutWithNode[] => {
  const totalDiff = endAt.diff(startAt);
  return layout.map(item => {
    const { itemStartAtX, itemEndAtWidth } = convertItemToGrid({
      item,
      cols,
      maxRange: { startAt, endAt },
      totalDiff,
    });
    return {
      ...omit(item, ["startAt", "endAt"]),
      x: Math.round(itemStartAtX),
      h: 1,
      w: Math.round(itemEndAtWidth),
      y: 0,
      minH: 1,
      maxH: 1,
    };
  });
};

interface convertItemToScheduleProps extends DateRange {
  cols: number;
  item: LayoutWithNode;
  totalDiff: number;
}

interface convertToScheduleProps extends DateRange {
  cols: number;
  layout: LayoutWithNode[];
}

export const convertItemToSchedule = (
  props: convertItemToScheduleProps,
): ScheduleLayoutWithNode => {
  const { cols, item, startAt, totalDiff } = props;
  const itemStartAtPercent = item.x / cols;
  const itemStartAtOffset = totalDiff * itemStartAtPercent;
  const itemStartAt = roundToInterval(
    SCHEDULER_ROUNDING,
    startAt.clone().add(itemStartAtOffset, "ms"),
  )
    .seconds(0)
    .milliseconds(0);
  const itemEndAtPercent = item.w / cols;
  const itemEndAtOffset = totalDiff * itemEndAtPercent;
  const itemEndAt = roundToInterval(
    SCHEDULER_ROUNDING,
    startAt.clone().add(itemStartAtOffset, "ms").add(itemEndAtOffset, "ms"),
  )
    .subtract(1, "minute")
    .seconds(59)
    .milliseconds(999);

  return {
    ...omit(item, ["x", "y", "h", "w", "minH", "maxH", "moved", "static"]),
    startAt: itemStartAt,
    endAt: itemEndAt,
  };
};

export const convertToSchedule = ({
  cols,
  endAt,
  layout,
  startAt,
}: convertToScheduleProps): ScheduleLayoutWithNode[] => {
  const totalDiff = endAt.diff(startAt);
  return layout.map(item => {
    return convertItemToSchedule({ cols, endAt, item, startAt, totalDiff });
  });
};

interface useGetBoundsProps {
  cols: number;
  endAt: Moment;
  maxEndAt: Moment;
  maxStartAt: Moment;
  parentLayerWidth: number;
  startAt: Moment;
  totalMaxTime: number;
}

export const useGetBounds = ({
  cols,
  endAt,
  maxEndAt,
  maxStartAt,
  parentLayerWidth,
  startAt,
  totalMaxTime,
}: useGetBoundsProps) => {
  // Calculate Boundings
  const { boundsStartAtX, boundsWidth } = useMemo(() => {
    const { itemStartAtX, itemEndAtWidth } = convertItemToGrid({
      item: {
        endAt: roundToInterval(SCHEDULER_ROUNDING, endAt)
          .seconds(0)
          .milliseconds(0),
        startAt: roundToInterval(SCHEDULER_ROUNDING, startAt)
          .seconds(0)
          .milliseconds(0),
      },
      cols,
      maxRange: {
        startAt: maxStartAt,
        endAt: maxEndAt,
      },
      totalDiff: totalMaxTime,
    });
    return {
      boundsStartAtX: Math.round((itemStartAtX / cols) * parentLayerWidth),
      boundsWidth: Math.round((itemEndAtWidth / cols) * parentLayerWidth),
    };
  }, [
    cols,
    endAt,
    maxEndAt,
    maxStartAt,
    parentLayerWidth,
    startAt,
    totalMaxTime,
  ]);

  return {
    boundsStartAtX,
    boundsWidth,
  };
};

export const getScheduledDuration = (
  startAt: momentTz.Moment | null,
  endAt: momentTz.Moment | null,
) => {
  return startAt && endAt
    ? roundToInterval(SCHEDULER_ROUNDING, endAt).diff(
        roundToInterval(SCHEDULER_ROUNDING, startAt).seconds(0).milliseconds(0),
        "m",
      )
    : null;
};
